import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAllSchemas, useTriggerIngestion } from "@noa/api-client";
import { ConformitSyncConfigForm } from "./conformit-sync-config-form";
import { conformitSyncDefaultValues, conformitSyncSchema, type ConformitSyncForm } from "~/utils/conformit-sync-form";
import { Alert, Button, Card, message, Switch, Text, Title } from "~/components/ui";

type ConformitSyncPanelProps = {
  onNavigateToHistory?: () => void;
};

export function ConformitSyncPanel({ onNavigateToHistory }: ConformitSyncPanelProps) {
  const { t } = useTranslation("admin");
  const [lastJobId, setLastJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { control, handleSubmit, watch } = useForm<ConformitSyncForm>({
    resolver: zodResolver(conformitSyncSchema),
    defaultValues: conformitSyncDefaultValues,
  });
  const withVision = watch("withVision");

  const { schemas, isLoading: schemasLoading } = useAllSchemas();

  const triggerSync = useTriggerIngestion();

  const handleSync = (values: ConformitSyncForm) => {
    triggerSync.mutate(
      {
        files: ["*"],
        source_type: "conformit",
        schema_id: values.schemaId || undefined,
        with_vision: values.withVision,
        skip_existing: values.skipExisting,
        max_records: values.limitEnabled ? values.maxRecords : undefined,
      },
      {
        onSuccess: (data) => {
          setLastJobId(data.job_id);
          setError(null);
          void message.success(t("uploadAdmin.sync.conformit.started"));
        },
        onError: () => {
          setError(t("uploadAdmin.sync.conformit.error"));
        },
      }
    );
  };

  const handleInvalid = () => {
    void message.error(t("uploadAdmin.sync.invalidForm"));
  };

  const schemaOptions = schemas.map((s) => ({
    value: s.schema_id,
    label: `${s.schema_id} — ${s.description || s.doc_type}`,
  }));

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ShieldCheck size={30} className="text-orange-500" />
        <div>
          <Title level={4} className="!mb-0">
            {t("uploadAdmin.sync.conformit.title")}
          </Title>
          <Text tone="muted">{t("uploadAdmin.sync.conformit.description")}</Text>
        </div>
      </div>

      {/* Sync Action Card */}
      <Card size="small" className="bg-noa-hover">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1">
            <Title level={5} className="!mb-1">
              {t("uploadAdmin.sync.conformit.syncTitle")}
            </Title>
            <Text tone="muted">{t("uploadAdmin.sync.conformit.syncDescription")}</Text>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <Text className="text-xs">{t("uploadAdmin.form.withVision")}</Text>
                <Controller
                  name="withVision"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      size="small"
                      checked={field.value}
                      onChange={field.onChange}
                      aria-label={t("uploadAdmin.form.withVision")}
                    />
                  )}
                />
              </div>
              {!withVision && (
                <Text className="text-xs text-orange-600 text-right max-w-48">
                  ⚠️ {t("uploadAdmin.sync.maximo.visionWarning")}
                </Text>
              )}
            </div>
            <Button
              variant="primary"
              size="large"
              icon={<RefreshCw size={14} className={triggerSync.isPending ? "animate-spin" : undefined} />}
              loading={triggerSync.isPending}
              onClick={handleSubmit(handleSync, handleInvalid)}
              data-testid="conformit-sync-button"
            >
              {t("uploadAdmin.sync.conformit.startSync")}
            </Button>
          </div>
        </div>
      </Card>

      <ConformitSyncConfigForm control={control} schemaOptions={schemaOptions} schemasLoading={schemasLoading} />

      {/* Error message */}
      {error && (
        <Alert
          type="error"
          message={t("common:error")}
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* Success message */}
      {lastJobId && (
        <Alert
          type="success"
          message={t("uploadAdmin.sync.taskStarted")}
          description={
            <div className="inline-flex flex-col gap-2">
              <Text>
                {t("uploadAdmin.sync.jobId")}: <code className="bg-noa-hover px-2 py-1 rounded">{lastJobId}</code>
              </Text>
              {onNavigateToHistory && (
                <Button variant="link" size="small" className="!p-0 !h-auto" onClick={onNavigateToHistory}>
                  {t("uploadAdmin.sync.viewInHistory")} →
                </Button>
              )}
            </div>
          }
          showIcon
          closable
          onClose={() => setLastJobId(null)}
        />
      )}

      {/* How it works */}
      <Card title={t("uploadAdmin.sync.conformit.howItWorks")} size="small">
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 list-decimal pl-5 text-noa-muted">
          <li>{t("uploadAdmin.sync.conformit.step1")}</li>
          <li>{t("uploadAdmin.sync.conformit.step2")}</li>
          <li>{t("uploadAdmin.sync.conformit.step3")}</li>
          <li>{t("uploadAdmin.sync.conformit.step4")}</li>
        </ol>
      </Card>
    </div>
  );
}
